<script>
import { Editor, EditorContent } from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';
import { debounce } from '@chatwoot/utils';
import { Attribute } from './Attribute.js';
import NextButton from 'dashboard/components-next/button/Button.vue';
import LoadingIndicator from 'shared/components/LoadingIndicator.vue';
import markdownToHtmlConverter from './MarkdownToHtmlConverter.js';
import htmlToMarkdownConverter from './HtmlToMarkdownConverter.js';

export default {
  components: {
    EditorContent,
    NextButton,
    LoadingIndicator,
  },
  props: {
    instruction: {
      type: Object,
      required: true,
    },
    showDeleteButton: {
      type: Boolean,
      default: true,
    },
    maxLength: {
      type: Number,
      default: 1000,
    },
  },
  emits: ['update', 'delete'],
  data() {
    return {
      editor: null,
      debouncedUpdate: null,
      isAutoSaving: false,
      librariesInitialized: false,
      librariesFetchAttempted: false,
      fallbackTimeout: null,
    };
  },
  computed: {
    characterCount() {
      if (!this.editor) return 0;
      return this.editor.state.doc.textContent.length;
    },
    isOverLimit() {
      return this.characterCount > this.maxLength;
    },
    isNearLimit() {
      return this.characterCount > this.maxLength * 0.8;
    },
    remainingCharacters() {
      return this.maxLength - this.characterCount;
    },
    characterCountText() {
      return `${this.characterCount}`;
    },
    // Check if libraries, tools, and attributes have been fetched
    areLibrariesLoaded() {
      const knowledgebaseUiFlags =
        this.$store.getters['knowledgebase/getUIFlags'];
      const toolsUiFlags = this.$store.getters['tools/getUIFlags'];
      const attributesUiFlags = this.$store.getters['attributes/getUIFlags'];

      // Consider all data loaded if:
      // 1. Not currently fetching AND we've attempted to fetch
      // 2. OR if we've already initialized (fallback)
      return (
        (!knowledgebaseUiFlags.isFetching &&
          !toolsUiFlags.isFetchingAvailable &&
          !attributesUiFlags.isFetching &&
          this.librariesFetchAttempted) ||
        this.librariesInitialized
      );
    },
  },
  watch: {
    // Watch for libraries, tools, and attributes to be loaded and initialize editor when ready
    areLibrariesLoaded(newValue) {
      if (newValue && !this.librariesInitialized) {
        this.librariesInitialized = true;
        // Clear the fallback timeout since we're initializing now
        if (this.fallbackTimeout) {
          clearTimeout(this.fallbackTimeout);
          this.fallbackTimeout = null;
        }
        this.initializeEditor();
      }
    },
  },
  async mounted() {
    // Try to fetch libraries, tools, and attributes if not already loaded/fetching
    const knowledgebaseUiFlags =
      this.$store.getters['knowledgebase/getUIFlags'];
    const toolsUiFlags = this.$store.getters['tools/getUIFlags'];
    const attributesUiFlags = this.$store.getters['attributes/getUIFlags'];

    // Fetch knowledgebase if not already loading
    if (!knowledgebaseUiFlags.isFetching) {
      try {
        await this.$store.dispatch('knowledgebase/get');
      } catch (error) {
        // Ignore error and proceed
      }
    }

    // Fetch tools if not already loading
    if (!toolsUiFlags.isFetchingAvailable) {
      try {
        await this.$store.dispatch('tools/getAvailable');
      } catch (error) {
        // Ignore error and proceed
      }
    }

    // Fetch attributes if not already loading
    if (!attributesUiFlags.isFetching) {
      try {
        await this.$store.dispatch('attributes/get');
      } catch (error) {
        // Ignore error and proceed
      }
    }

    this.librariesFetchAttempted = true;

    // Initialize editor if data is ready or after a short delay
    if (this.areLibrariesLoaded) {
      this.librariesInitialized = true;
      this.initializeEditor();
    } else {
      // Fallback: Initialize editor after 1 second regardless of data state
      // to prevent indefinite waiting
      this.fallbackTimeout = setTimeout(() => {
        if (!this.librariesInitialized) {
          this.librariesInitialized = true;
          this.initializeEditor();
        }
      }, 1000);
    }
  },
  beforeUnmount() {
    this.destroyEditor();
    if (this.debouncedUpdate && this.debouncedUpdate.cancel) {
      this.debouncedUpdate.cancel();
    }
    if (this.fallbackTimeout) {
      clearTimeout(this.fallbackTimeout);
    }
  },
  methods: {
    initializeEditor() {
      if (this.editor) {
        this.destroyEditor();
      }

      this.debouncedUpdate = debounce(this.onContentChange, 500);

      const content = markdownToHtmlConverter.convertToHtml(
        this.instruction.content || '',
        this.$store
      );

      this.editor = new Editor({
        content: content || '',
        extensions: [StarterKit, Attribute],
        onUpdate: () => {
          this.debouncedUpdate();
        },
        editorProps: {
          attributes: {
            class: 'instruction-editor-content',
            'data-placeholder': 'Enter instruction content...',
          },
        },
      });

      this.$nextTick(() => {
        if (this.editor) {
          this.editor.commands.focus('end');
        }
      });
    },
    destroyEditor() {
      if (this.editor) {
        this.editor.destroy();
        this.editor = null;
      }
    },
    onContentChange() {
      if (!this.editor) return;

      this.isAutoSaving = true;

      // Convert TipTap HTML to markdown for storage
      const htmlContent = this.editor.getHTML();
      const markdownContent =
        htmlToMarkdownConverter.convertToMarkdown(htmlContent);

      this.$emit('update', {
        ...this.instruction,
        content: markdownContent,
      });

      setTimeout(() => {
        this.isAutoSaving = false;
      }, 800);
    },
    handleDelete() {
      this.$emit('delete', this.instruction);
    },
  },
};
</script>

<template>
  <div
    class="instruction-item"
    data-testid="instruction-item"
    :class="{ group: showDeleteButton }"
  >
    <div class="instruction-editor">
      <!-- Delete button moved to top-right corner (conditionally shown) -->
      <div v-if="showDeleteButton" class="flex justify-end mb-2">
        <div class="opacity-0 group-hover:opacity-100 transition-opacity">
          <NextButton
            v-tooltip.top="$t('ASSISTANT_MGMT.DELETE.BUTTON_TEXT')"
            icon="i-lucide-x"
            ghost
            sm
            data-testid="instruction-delete-button"
            @click="handleDelete"
          />
        </div>
      </div>

      <div class="editor-container">
        <div v-if="!editor" class="editor-loading-placeholder">
          <div class="skeleton-lines">
            <div class="skeleton-line" />
            <div class="skeleton-line skeleton-line-short" />
          </div>
        </div>

        <EditorContent
          :editor="editor"
          class="editor-content"
          data-testid="instruction-editor-content"
        />

        <!-- Character count indicator positioned in bottom right corner -->
        <div
          v-if="editor"
          class="absolute bottom-2 right-3 bg-white/90 dark:bg-n-slate-11/90 backdrop-blur-sm rounded-md px-2 py-1 text-xs font-medium pointer-events-none z-10 shadow-sm border border-n-slate-3 dark:border-n-slate-8"
        >
          <span
            :class="{
              'text-n-slate-10 dark:text-n-slate-10':
                !isNearLimit && !isOverLimit,
              'text-amber-600 dark:text-amber-400': isNearLimit && !isOverLimit,
              'text-red-500 dark:text-red-400': isOverLimit,
            }"
          >
            {{ characterCountText }}
          </span>
        </div>
      </div>
    </div>

    <LoadingIndicator
      v-if="isAutoSaving"
      position="fixed"
      placement="bottom-right"
      variant="primary"
      size="md"
      :message="$t('ASSISTANT_MGMT.AUTO_SAVING') || 'Auto-saving...'"
    />
  </div>
</template>

<style scoped>
.instruction-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.instruction-editor {
  position: relative;
}

.editor-container {
  position: relative;
  border: 1px solid var(--color-border-light, #e5e7eb);
  border-radius: 0.375rem;
  background-color: white;
  overflow: hidden;
  padding: 0 12px 12px 12px;
  min-height: 112px; /* Reserve height for editor (80px min-height + padding) */
}

.dark .editor-container {
  border: 1px solid rgba(255, 255, 255, 0.2);
  background-color: rgba(0, 0, 0, 0.2);
}

.editor-loading-placeholder {
  min-height: 80px;
  display: flex;
  align-items: flex-start;
  padding: 0.75rem;
}

.skeleton-lines {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.skeleton-line {
  height: 1rem;
  background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s infinite;
  border-radius: 0.25rem;
}

.skeleton-line-short {
  width: 60%;
}

.dark .skeleton-line {
  background: linear-gradient(90deg, #374151 25%, #4b5563 50%, #374151 75%);
  background-size: 200% 100%;
}

@keyframes skeleton-loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>

<style>
.instruction-editor-content {
  outline: none;
  padding: 0.75rem;
  min-height: 80px;
  width: 100%;
  overflow-y: auto;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen,
    Ubuntu, Cantarell, sans-serif;
  line-height: 1.6;
  color: inherit;
}

.instruction-editor-content p.is-editor-empty:first-child::before {
  content: attr(data-placeholder);
  float: left;
  color: #9ca3af;
  pointer-events: none;
  height: 0;
}

.instruction-editor-content p {
  margin: 0.5em 0;
}

.instruction-editor-content p:first-child {
  margin-top: 0;
}

.instruction-editor-content p:last-child {
  margin-bottom: 0;
}

.instruction-editor-content ul,
.instruction-editor-content ol {
  padding: 0 1rem;
  margin: 1rem 0;
}

.instruction-editor-content h1,
.instruction-editor-content h2,
.instruction-editor-content h3,
.instruction-editor-content h4,
.instruction-editor-content h5,
.instruction-editor-content h6 {
  line-height: 1.1;
  margin-top: 1rem;
  margin-bottom: 0.5rem;
}

.instruction-editor-content h1 {
  font-size: 1.4rem;
}
.instruction-editor-content h2 {
  font-size: 1.2rem;
}
.instruction-editor-content h3 {
  font-size: 1.1rem;
}

.instruction-editor-content code {
  background-color: #f3f4f6;
  border-radius: 0.25rem;
  color: #374151;
  font-size: 0.875rem;
  padding: 0.125rem 0.25rem;
  font-family: 'SF Mono', Consolas, 'Liberation Mono', Menlo, monospace;
}

.instruction-editor-content pre {
  background: #f8f9fa;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  color: #374151;
  font-family: 'SF Mono', Consolas, 'Liberation Mono', Menlo, monospace;
  margin: 1.5rem 0;
  padding: 1rem;
}

.instruction-editor-content pre code {
  background: none;
  color: inherit;
  font-size: 0.875rem;
  padding: 0;
  border: none;
}

.instruction-editor-content blockquote {
  border-left: 3px solid rgba(13, 13, 13, 0.1);
  margin: 1.5rem 0;
  padding-left: 1rem;
}

.instruction-editor-content hr {
  border: none;
  border-top: 1px solid rgba(13, 13, 13, 0.1);
  margin: 2rem 0;
}

/* Attribute pill styles */
.instruction-editor-content .attribute-pill {
  display: inline-flex;
  align-items: center;
  background-color: #e0f2fe;
  color: #0284c7;
  border-radius: 1rem;
  padding: 0.25rem 0.75rem;
  font-size: 0.875rem;
  font-weight: 500;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
  cursor: help;
  position: relative;
}

.instruction-editor-content .attribute-pill:hover {
  transform: translateY(-1px);
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  background-color: #bae6fd;
}

/* Custom tooltip styles */
.instruction-editor-content
  .attribute-pill[data-tooltip]:not([data-tooltip='']):hover::after {
  content: attr(data-tooltip);
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 8px;
  padding: 8px 12px;
  background-color: #1f2937;
  color: white;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1.2;
  white-space: nowrap;
  max-width: 200px;
  white-space: normal;
  word-wrap: break-word;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  pointer-events: none;
}

/* Tooltip arrow */
.instruction-editor-content
  .attribute-pill[data-tooltip]:not([data-tooltip='']):hover::before {
  content: '';
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 2px;
  border: 4px solid transparent;
  border-top-color: #1f2937;
  z-index: 1001;
  pointer-events: none;
}
</style>
